// Parses the `ddforward` value the Datadog browser SDK appends to a `proxy` URL
// and turns it into the one upstream URL a RUM intake relay may call.
//
// The SDK sends `<proxy>?ddforward=<encodeURIComponent('/api/v2/rum?<params>')>`.
// The upstream host is never taken from the request: it is the browser-intake
// host the fixed site map gives for the site the caller passes in. Only the RUM track is accepted, so the
// relay cannot be pointed at logs, replay, or any other path or host. The query
// is re-built from parsed parameters rather than concatenated as raw text.
import { intakeHostForSite } from '../datadog-intake-hosts';

export const RUM_INTAKE_PATH = '/api/v2/rum';
const ENCODING_PARAM = 'dd-evp-encoding';

export type IntakeTargetRejection = 'missing' | 'malformed' | 'path' | 'site';

export interface IntakeTarget {
  readonly ok: true;
  /** `https://<intake host>/api/v2/rum?<params>`, params in their original order. */
  readonly upstreamUrl: URL;
  /** The batch body is zlib-deflated (`dd-evp-encoding=deflate`). */
  readonly deflate: boolean;
}

export type IntakeTargetResult =
  | IntakeTarget
  | { readonly ok: false; readonly reason: IntakeTargetRejection };

// Any C0 control character or DEL. Built from code points so the pattern
// itself carries no literal control characters.
const CONTROL_CHARS = new RegExp(
  `[${String.fromCharCode(0)}-${String.fromCharCode(0x1f)}${String.fromCharCode(0x7f)}]`,
);

/**
 * @param rawDdforward the `ddforward` query value exactly as it appears in the
 *   request URL, still percent-encoded. Decoding happens here so that an
 *   undecodable value is rejected rather than passed through by a lenient
 *   framework decoder.
 */
export function parseIntakeTarget(rawDdforward: unknown, site: string): IntakeTargetResult {
  if (typeof rawDdforward !== 'string' || rawDdforward === '')
    return { ok: false, reason: 'missing' };

  let decoded: string;
  try {
    decoded = decodeURIComponent(rawDdforward);
  } catch {
    return { ok: false, reason: 'malformed' };
  }
  if (CONTROL_CHARS.test(decoded) || decoded.includes('#') || decoded.includes('\\')) {
    return { ok: false, reason: 'malformed' };
  }
  // A leading `/api/v2/rum?` rules out absolute (`https://…`) and
  // protocol-relative (`//host/…`) targets before any URL parsing happens.
  if (!decoded.startsWith(`${RUM_INTAKE_PATH}?`) || decoded.includes('..')) {
    return { ok: false, reason: 'path' };
  }

  const host = intakeHostForSite(site);
  if (host === undefined) return { ok: false, reason: 'site' };

  const queryText = decoded.slice(RUM_INTAKE_PATH.length + 1);
  const params = new URLSearchParams(queryText);
  const upstreamUrl = new URL(`https://${host}${RUM_INTAKE_PATH}`);
  for (const [key, value] of params) upstreamUrl.searchParams.append(key, value);
  if (upstreamUrl.host !== host || upstreamUrl.pathname !== RUM_INTAKE_PATH) {
    return { ok: false, reason: 'path' };
  }
  return { ok: true, upstreamUrl, deflate: params.get(ENCODING_PARAM) === 'deflate' };
}

/** The same upstream URL with `dd-evp-encoding` removed, for a body forwarded inflated. */
export function withoutEncoding(upstreamUrl: URL): URL {
  const next = new URL(upstreamUrl);
  next.searchParams.delete(ENCODING_PARAM);
  return next;
}
